import React, { useState, useRef, useEffect } from "react";
import faqData from "../pages/Querries";
import "../styles/Chatbot.css";
import { Send, X } from "lucide-react";
import berymoLogo from "../assets/images/berymo3.png";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [otherMode, setOtherMode] = useState(false);
  const inputRef = useRef(null);

  const messagesEndRef = useRef(null);

  const initialQuickReplies = [
    "What is Berymo?",
    "What types of rides are available?",
    "How does the ride process work?",
    "How do I track my order?",
    "How do I contact support?",
    "Other",
  ];

  const [messages, setMessages] = useState([
    {
      text: "Hi! Welcome to Berymo Support.\n## How can I help you today?",
      isBot: true,
      quickReplies: initialQuickReplies,
    },
  ]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  const findAnswer = (text) => {
    const lower = text.toLowerCase().trim();
    for (const item of faqData) {
      if (item?.keywords?.some((k) => k.toLowerCase() === lower)) return item;
    }
    for (const item of faqData) {
      if (item?.keywords?.some((k) => lower.includes(k.toLowerCase()))) return item;
    }
    return null; // no auto-fallback; handled by otherMode
  };

  const sendMessage = (text = input) => {
    const messageText = typeof text === "string" ? text : input;
    if (!messageText || !messageText.trim()) return;

    setMessages((prev) => [...prev, { text: messageText, isBot: false }]);
    setInput("");
    setIsTyping(true);

    setTimeout(() => {
      const res = findAnswer(messageText);
      if (res) {
        setMessages((prev) => [
          ...prev,
          {
            text: res.answer,
            isBot: true,
            quickReplies: res.quickReplies || [],
          },
        ]);
        setOtherMode(false);
      } else if (otherMode) {
        setMessages((prev) => [
          ...prev,
          {
            text: "Thanks for the details. Our team will contact you soon!",
            isBot: true,
            quickReplies: [],
          },
        ]);
        setOtherMode(false);
      } else {
        // No reply for unmatched non-"Other" inputs
      }
      setIsTyping(false);
    }, 800);
  };

  const renderMessageContent = (text) =>
    text.split("\n").map((line, i) => {
      if (line.startsWith("##")) {
        return (
          <h4 key={i} className="msg-title">
            {line.replace("##", "").trim()}
          </h4>
        );
      }
      return (
        <p key={i} style={{ margin: 0 }}>
          {line}
        </p>
      );
    });

  return (
    <div className="chatbot-root">
      {isOpen && (
        <div className="chatbot-container">
          {/* Header */}
          <div className="chatbot-header">
            <div className="header-left">
              <div className="header-avatar">
                <img src={berymoLogo} alt="Berymo" className="header-logo" />
              </div>
              <div className="header-info">
                <span className="header-name">Berymo Support</span>
                <span className="header-status">
                  <span className="status-dot" />
                  Online · Always here to help
                </span>
              </div>
            </div>
            <button className="close-btn" onClick={() => setIsOpen(false)}>
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>

          {/* Messages */}
          <div className="messages">
            {messages.map((m, i) => (
              <div key={i} className={`message-row ${m.isBot ? "bot" : "user"}`}>
                <div className={m.isBot ? "bot-msg" : "user-msg"}>
                  {renderMessageContent(m.text)}
                </div>
                {m.isBot && i === messages.length - 1 && m.quickReplies?.length > 0 && (
                  <div className="quick-replies">
                    {m.quickReplies.map((q, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (q.toLowerCase() === "other") {
                            inputRef.current?.focus();
                            setOtherMode(true);
                            return;
                          }
                          setOtherMode(false);
                          sendMessage(q);
                        }}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {isTyping && (
              <div className="message-row bot">
                <div className="typing">
                  <div className="typing-dots">
                    <span />
                    <span />
                    <span />
                  </div>
                  <span className="typing-label">Berymo is typing</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="input-area">
            <input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder="Ask me anything…"
            />
            <button className="send-btn" onClick={() => sendMessage()}>
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {!isOpen && (
        <div className="fab-wrapper">
          <button className="chatbot-fab" onClick={() => setIsOpen(true)}>
            <img src={berymoLogo} alt="Berymo" className="fab-logo" />
          </button>
          <span className="fab-label-outside">Berymo Bot</span>
        </div>
      )}
    </div>
  );
};

export default Chatbot;
